/**
 * Analytics utility functions for Flowcus ADHD app
 * Uses PostHog for comprehensive user behavior tracking
 */

export interface AnalyticsEvent {
  // Task Management Events
  task_created: {
    task_type: string;
    estimated_duration?: number;
    priority: "low" | "medium" | "high" | "urgent";
    has_deadline: boolean;
    created_via: "voice" | "text" | "template" | "ai";
    adhd_energy_level?: "low" | "medium" | "high";
    adhd_context_cost?: number;
    adhd_hyperfocus_compatible?: boolean;
  };

  task_completed: {
    task_id: string;
    actual_duration?: number;
    completion_rate: number;
    focus_interruptions?: number;
    used_timer: boolean;
    energy_level_match?: boolean; // Did actual energy match predicted
  };

  task_abandoned: {
    task_id: string;
    progress_percentage: number;
    abandonment_reason: string;
    time_of_day: number;
  };

  // Focus Session Events
  focus_session_started: {
    session_type: "pomodoro" | "timeblock" | "hyperfocus" | "short_focus";
    planned_duration: number;
    energy_level: "low" | "medium" | "high";
    task_count?: number;
    time_of_day?: number;
    day_of_week?: number;
  };

  focus_session_completed: {
    session_type: "pomodoro" | "timeblock" | "hyperfocus" | "short_focus";
    actual_duration: number;
    planned_duration: number;
    completion_rate: number;
    interruption_count: number;
    session_id?: string;
    tasks_completed?: number;
    break_taken?: number;
    productivity_score?: number;
  };

  // Gamification Events
  level_up: {
    new_level: number;
    total_xp: number;
    xp_gained: number;
    time_to_level: number; // days since last level
  };

  achievement_unlocked: {
    achievement_id: string;
    achievement_name?: string;
    achievement_type?: string;
    category?: string;
    rarity: "common" | "uncommon" | "rare" | "epic" | "legendary";
  };

  streak_maintained: {
    streak_type: "daily_tasks" | "focus_sessions" | "login";
    current_streak: number;
    longest_streak: number;
    streak_bonus_xp: number;
  };

  // AI Interaction Events
  ai_interaction: {
    interaction_type: "voice_command" | "text_input" | "function_call";
    intent: string;
    success: boolean;
    response_time_ms: number;
  };

  // Overwhelm Detection Events
  overwhelm_detected: {
    trigger: "task_count" | "deadline_pressure" | "user_reported";
    task_backlog: number;
    stress_indicators: string[];
    suggested_intervention: string;
  };

  // Timer Events
  timer_started: {
    timer_type: "pomodoro" | "custom" | "focus";
    duration_minutes: number;
    background_sounds?: boolean;
  };

  timer_completed: {
    timer_type: "pomodoro" | "custom" | "focus";
    duration_minutes: number;
    actual_duration_minutes: number;
    interruptions: number;
  };

  // AI Suggestion Events
  ai_suggestion_accepted: {
    suggestion_type: string;
    suggestion_quality_score?: number;
    time_to_accept?: number;
  };

  // XP Events
  xp_earned: {
    amount: number;
    source: string;
    category?: string;
    multiplier?: number;
    current_level?: number;
    next_level_progress?: number;
  };

  analytics_export_requested: {
    format: "json" | "csv" | "pdf";
    time_range: string;
    sections?: string[];
  };

  analytics_share_requested: {
    method: "email" | "link" | "clipboard";
    content_type: "summary" | "full_report";
    time_range: string;
  };
}

/**
 * Simple analytics tracking function that works without hooks
 * This creates a global analytics object that can be imported anywhere
 */
class FlowcusAnalytics {
  private isEnabled = false;

  constructor() {
    // Check if PostHog is available globally (set by PostHogProvider)
    this.isEnabled = typeof window !== "undefined" && !!(window as any).posthog;
  }

  /**
   * Track an event with typed properties
   */
  track<K extends keyof AnalyticsEvent>(eventName: K, properties: AnalyticsEvent[K]) {
    if (!this.isEnabled || typeof window === "undefined") return;

    const posthog = (window as any).posthog;
    if (!posthog) return;

    posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
      app_version: import.meta.env.VITE_VERSION || "1.0.0",
      platform: "web",
    });
  }

  /**
   * Track page views
   */
  trackPageView(pageName: string, properties?: Record<string, any>) {
    if (!this.isEnabled || typeof window === "undefined") return;

    const posthog = (window as any).posthog;
    if (!posthog) return;

    posthog.capture("$pageview", {
      $current_url: window.location.href,
      page_name: pageName,
      ...properties,
    });
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.isEnabled || typeof window === "undefined") return;

    const posthog = (window as any).posthog;
    if (!posthog) return;

    posthog.setPersonProperties(properties);
  }

  /**
   * Track errors for debugging
   */
  trackError(error: Error, context?: Record<string, any>) {
    if (!this.isEnabled || typeof window === "undefined") return;

    const posthog = (window as any).posthog;
    if (!posthog) return;

    posthog.capture("error_occurred", {
      error_name: error.name,
      error_message: error.message,
      error_stack: error.stack,
      context,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(featureName: string, action: string, metadata?: Record<string, any>) {
    if (!this.isEnabled || typeof window === "undefined") return;

    const posthog = (window as any).posthog;
    if (!posthog) return;

    posthog.capture("feature_used", {
      feature_name: featureName,
      action,
      metadata,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Check if analytics is enabled
   */
  get enabled() {
    return this.isEnabled;
  }
}

// Export singleton instance
export const analytics = new FlowcusAnalytics();

// Re-export types and hooks
export type { ADHDUserProperties, ADHDAnalyticsEvent } from "./posthog-client";
export * from "./posthog-hooks";
